import { Link } from 'react-router-dom';

import { useTicketMetrics } from '@/lib/api/ticketHooks';

/** Support metrics — ticket load by stage, assignment split, and resolution
 *  throughput. The submenu's landing page (Linear's "Home" slot). */

const STAGE_LABELS: Record<string, string> = {
  needs_intake: 'Needs intake',
  awaiting_rx: 'Awaiting Rx',
  ops_hold: 'Held for ops',
  at_pharmacy: 'Awaiting shipment',
  warning: 'Pharmacy warnings',
  unhandled_webhook: 'Unhandled webhooks',
  manual: 'Manual',
  other: 'Other',
};

export function SupportMetricsScreen() {
  const { data, isLoading, error } = useTicketMetrics();

  if (isLoading) {
    return <div className="type-label-sm text-ink-muted py-40">Loading metrics…</div>;
  }
  if (error || !data) {
    return <div className="type-label-sm text-danger py-40">Failed to load metrics.</div>;
  }

  const tiles = [
    { label: 'Open tickets', value: data.open_total },
    { label: 'Unassigned', value: data.unassigned, danger: data.unassigned > 0 },
    { label: 'Assigned', value: data.assigned },
    { label: 'Overdue', value: data.overdue, danger: data.overdue > 0 },
    { label: 'Resolved · 24h', value: data.resolved_last_24h },
    { label: 'Resolved · 7d', value: data.resolved_last_7d },
  ];

  return (
    <div className="flex flex-col gap-20">
      <div className="flex items-center">
        <h1 className="type-headline-md">Support metrics</h1>
        <Link to="/admin/support" className="type-label-sm text-accent hover:underline ml-auto">
          Open board →
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-10">
        {tiles.map((t) => (
          <div key={t.label} className="p-12 border border-line rounded-lg bg-raised">
            <p className="text-10 uppercase text-ink-muted">{t.label}</p>
            <p className={`type-heading-md mt-4 ${t.danger ? 'text-danger' : ''}`}>{t.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
        <section className="border border-line rounded-lg bg-raised overflow-hidden">
          <div className="px-16 py-12 border-b border-line">
            <h2 className="type-label-md font-semibold">Open tickets by stage</h2>
          </div>
          <table className="w-full text-left type-label-sm [&_th]:px-16 [&_th]:py-8 [&_td]:px-16 [&_td]:py-8">
            <tbody>
              {Object.entries(data.by_stage)
                .sort(([, a], [, b]) => b - a)
                .map(([stage, count]) => (
                  <tr key={stage} className="border-t border-line">
                    <td>{STAGE_LABELS[stage] ?? stage}</td>
                    <td className="w-[6rem] text-right font-mono">{count}</td>
                  </tr>
                ))}
              {Object.keys(data.by_stage).length === 0 && (
                <tr>
                  <td className="text-ink-muted">No open tickets 🎉</td>
                </tr>
              )}
            </tbody>
          </table>
        </section>

        <section className="border border-line rounded-lg bg-raised overflow-hidden">
          <div className="px-16 py-12 border-b border-line">
            <h2 className="type-label-md font-semibold">Assignee load</h2>
          </div>
          <table className="w-full text-left type-label-sm [&_th]:px-16 [&_th]:py-8 [&_td]:px-16 [&_td]:py-8">
            <tbody>
              <tr className="border-t border-line">
                <td className="text-ink-muted">Unassigned</td>
                <td className="w-[6rem] text-right font-mono">{data.unassigned}</td>
              </tr>
              {data.by_assignee.map((a) => (
                <tr key={a.id} className="border-t border-line">
                  <td>{a.name}</td>
                  <td className="w-[6rem] text-right font-mono">{a.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
}
